import { useEffect, useState } from "react";
import { useRuntimeConfig } from "@/hooks/useRuntimeConfig";
import { isLive } from "@/libs/clientConfig";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "pricepoint-panel": {
        panel?: string;
        intent?: string;
        year?: string | number;
        make?: string;
        model?: string;
        trim?: string;
        zip?: string;
        mileage?: string | number;
        price?: string | number;
        "access-key"?: string;
      };
    }
  }
}

let embedScriptPromise: Promise<void> | null = null;

/**
 * Origins embed.js may be loaded from. The script executes with full
 * privileges on this origin and its URL arrives from runtime config, so
 * permitted origins are pinned here rather than trusting the config value
 * outright — a bad or tampered config then degrades exactly like a network
 * failure instead of running arbitrary JS.
 *
 * Production apexes are accepted unconditionally; the padev host and the
 * local PricePoint dev server only when this page is NOT served from the
 * production host.
 */
const ALLOWED_PRICEPOINT_ORIGIN =
  /^https:\/\/pricepoint\.(privateauto\.com|dealnow\.com)$/;
const NON_PROD_PRICEPOINT_ORIGIN = /^https:\/\/pricepoint\.padev\.xyz$/;
const LOCAL_DEV_ORIGIN = "http://localhost:5173";

function isAllowedOrigin(
  origin: string,
  allowNonProdOrigins: boolean
): boolean {
  if (ALLOWED_PRICEPOINT_ORIGIN.test(origin)) return true;
  if (!allowNonProdOrigins) return false;
  return NON_PROD_PRICEPOINT_ORIGIN.test(origin) || origin === LOCAL_DEV_ORIGIN;
}

/**
 * Load PricePoint's embed.js once per page. The tag must be a CLASSIC script
 * (not type="module"): module tags are CORS-mode fetches the static origin
 * doesn't serve, and the classic tag provides document.currentScript, from
 * which the bundle derives the absolute API base so panels on our pages call
 * the PricePoint API rather than our /api.
 */
function loadPricepointEmbedScript(
  pricepointUrl: string | undefined,
  allowNonProdOrigins: boolean
): Promise<void> {
  // Validate BEFORE the memo so every caller's origin is checked, not just
  // the first one's.
  let origin: string;
  try {
    origin = new URL(pricepointUrl ?? "").origin;
  } catch {
    return Promise.reject(new Error("PricePoint host is not a valid URL"));
  }
  if (!isAllowedOrigin(origin, allowNonProdOrigins)) {
    return Promise.reject(
      new Error(`PricePoint host is not an allowed origin: ${origin}`)
    );
  }

  if (embedScriptPromise) {
    return embedScriptPromise;
  }

  embedScriptPromise = new Promise<void>((resolve, reject) => {
    if (window.customElements?.get("pricepoint-panel")) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `${origin}/embed.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the dead tag and allow a later retry instead of caching the
      // failure (and accumulating <script> elements) forever.
      script.remove();
      embedScriptPromise = null;
      reject(new Error("Failed to load PricePoint embed script"));
    };
    document.head.appendChild(script);
  });

  return embedScriptPromise;
}

type Status = "loading" | "ready" | "failed" | "off";

interface Props {
  listing: any;
}

/**
 * Host for PricePoint's <pricepoint-panel> web component. The element's
 * attributes are live — changing a bound value refetches that panel's data —
 * so this host only loads the embed script once and forwards vehicle
 * attributes; all rendering happens inside the component's shadow root.
 *
 * The corridor answers "how does this price compare to the LOCAL market" —
 * without a zip it cannot be computed, and an environment without the
 * pricepoint runtime config simply has the feature off; both collapse the
 * section quietly rather than mounting an error.
 */
const PricepointPanelSection = ({ listing }: Props) => {
  const { config } = useRuntimeConfig();
  const [status, setStatus] = useState<Status>("loading");

  const zipcode = listing?.listingLocation?.zipcode;
  const pricepointUrl = config?.pricepoint?.url;
  const accessKey = config?.pricepoint?.accessKey;

  useEffect(() => {
    if (!config) return; // runtime config still loading — keep the spinner
    if (!pricepointUrl || !zipcode) {
      setStatus("off");
      return;
    }
    let cancelled = false;
    // Fail closed on the prod gate: non-prod origins (padev, localhost) are
    // only allowed when this page is NOT served from the production host.
    loadPricepointEmbedScript(pricepointUrl, !isLive()).then(
      () => {
        if (!cancelled) setStatus("ready");
      },
      (error: Error) => {
        // Surface the reason: an allowlist rejection must be distinguishable
        // from PricePoint being down when someone debugs a blank panel.
        console.warn("PricePoint embed unavailable:", error?.message ?? error);
        if (!cancelled) setStatus("failed");
      }
    );
    return () => {
      cancelled = true;
    };
  }, [config, pricepointUrl, zipcode]);

  if (status === "off") return null;

  return (
    <div className="w-full px-4 md:px-0">
      <div className="relative w-full md:w-[820px] border border-[#e3e3e3] my-4 rounded-lg px-8 py-6">
        <p className="text-base font-semibold text-[#354360]">
          Pricing insights
        </p>
        <div className="relative md:mx-auto pt-3">
          {status === "failed" ? (
            <p className="text-base text-center py-4">
              We were unable to load pricing for this vehicle right now. Please
              try again later.
            </p>
          ) : status === "ready" ? (
            <pricepoint-panel
              panel="market-corridor"
              intent="sell"
              year={listing.RegistrationYear}
              make={listing.CarMake}
              model={listing.CarModel}
              trim={listing.Trim || undefined}
              zip={zipcode}
              mileage={listing.Mileage || undefined}
              price={listing.Price || undefined}
              access-key={accessKey || undefined}
            ></pricepoint-panel>
          ) : (
            <div className="flex justify-center py-6">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#354360]"
                role="status"
                aria-label="Loading"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricepointPanelSection;
